import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getCustomerEventAdminTableDataRetrieveQueryKey,
  useCustomerEventsRollback,
} from "gen/endpoints/customer-event-admin/customer-event-admin";
import { CustomerEventRowMetadata } from "gen/model/customerEventRowMetadata";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { ConfirmationDialogProps } from "shared/components/modal/confirm/ConfirmationDialog";
import { formatIsoDatetimeToDisplay } from "shared/utils/DateUtils";

export interface EventsRollbackConfirmDialogProps {
  fundFamilyId: string;
}

export interface EventsRollbackConfirmDialogArgs {
  events: CustomerEventRowMetadata[];
}

export const EventsRollbackConfirmDialog: React.FC<
  ConfirmationDialogProps<
    boolean,
    EventsRollbackConfirmDialogProps,
    EventsRollbackConfirmDialogArgs
  >
> = ({
  alertProps,
  onSuccess,
  onCancel,
  extraProps: { fundFamilyId },
  args: { events },
}) => {
  const { t } = useTranslation(["common", "events"]);
  const queryClient = useQueryClient();

  const { mutateAsync: deleteEvents } = useCustomerEventsRollback();

  const form = useForm();

  const onSubmit = async () => {
    const customerEventIds = events.map((e) => e.id);
    await deleteEvents({
      data: {
        fund_family_id: fundFamilyId,
        customer_event_ids: customerEventIds,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: getCustomerEventAdminTableDataRetrieveQueryKey({
        fund_family_id: fundFamilyId,
      }),
    });
    onSuccess(true);
  };

  const lastEvent = events.at(-1)!;

  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit}>
          <ModalHeader>
            {t("events:rollback.finalize.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <VStack spacing="16px" alignItems="flex-start">
              <Box>
                <Text>{t("events:rollback.finalize.description")}</Text>
              </Box>
              <Box>
                <Text textStyle="bodyBold">
                  {t("events:rollback.finalize.rollbackFrom")}
                </Text>
                <Text>
                  {t("events:rollback.finalize.eventSummary", {
                    createdAt: formatIsoDatetimeToDisplay(lastEvent.created_at),
                    type: lastEvent.event_type_text,
                    subType: lastEvent.event_sub_type_text,
                  })}
                </Text>
              </Box>
              <Box>
                <Text textStyle="bodyBold">
                  {t("events:rollback.finalize.includedEvents")}
                </Text>
                <Text>{events.length}</Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={() => onCancel()} />}
              confirm={
                <FormSubmitButton>
                  {t("events:rollback.finalize.submit")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
